import * as THREE from "three";

import type { LoomGpuImplicitUnionPlan } from "./loomGpuImplicitUnion";
import {
  LOOM_GPU_UNION_BACKEND,
  LOOM_GPU_UNION_FRAGMENT_POLICY,
} from "./loomGpuImplicitUnion";

export type LoomGpuUnionViewerState = "active" | "application-incomplete" | "inactive"
  | "unavailable" | "unsupported-mode";

export interface LoomGpuUnionApplicationReceipt {
  appliedMeshCount: number;
  fallbackReason: string | null;
}

interface LoomGpuUnionViewportMode {
  assemblyView: string;
  surfaceStyle: string;
}

export interface LoomGpuUnionViewResolution {
  candidateActive: boolean;
  fallbackReason: string | null;
  renderActive: boolean;
  state: LoomGpuUnionViewerState;
}

export type LoomGpuProgramRetainers = Map<string, THREE.Material>;

export function resolveLoomGpuUnionView(
  plan: LoomGpuImplicitUnionPlan | null,
  analyticPreviewActive: boolean,
  viewport: LoomGpuUnionViewportMode,
  application: LoomGpuUnionApplicationReceipt,
): LoomGpuUnionViewResolution {
  const modeSupported = viewport.assemblyView === "assembled" && viewport.surfaceStyle === "filled";
  const candidateActive = plan?.kind === "available" && !analyticPreviewActive && modeSupported;
  const fallbackReason = candidateActive ? application.fallbackReason : null;
  const resolvedFallbackReason = fallbackReason
    ?? (plan?.kind === "unavailable" ? plan.reason
      : plan?.kind === "available" && analyticPreviewActive ? "analytic explicit trim has priority"
        : plan?.kind === "available" && !modeSupported ? "GPU union requires assembled filled mode"
          : null);
  const state: LoomGpuUnionViewerState = !plan ? "inactive" : plan.kind === "unavailable" ? "unavailable"
    : analyticPreviewActive ? "inactive" : !modeSupported ? "unsupported-mode"
      : application.fallbackReason ? "application-incomplete" : "active";
  return { candidateActive, fallbackReason: resolvedFallbackReason, renderActive: state === "active", state };
}

export function loomGpuUnionDataAttributes(
  plan: LoomGpuImplicitUnionPlan | null,
  resolution: LoomGpuUnionViewResolution,
  application: LoomGpuUnionApplicationReceipt,
): Record<string, string | number> {
  const available = plan?.kind === "available" ? plan : null;
  return {
    "data-transient-union-backend": resolution.renderActive ? LOOM_GPU_UNION_BACKEND : "none",
    "data-gpu-union-state": resolution.state,
    "data-gpu-union-operand-count": available?.operandIds.length ?? 0,
    "data-gpu-union-plan-signature": available?.planSignature ?? "none",
    "data-gpu-union-program-signature": available?.programSignature ?? "none",
    "data-gpu-union-parameter-signature": available?.parameterSignature ?? "none",
    "data-gpu-union-fragment-policy": resolution.renderActive ? LOOM_GPU_UNION_FRAGMENT_POLICY : "none",
    "data-gpu-union-fallback-reason": resolution.fallbackReason ?? "none",
    "data-gpu-union-applied-mesh-count": resolution.renderActive ? application.appliedMeshCount : 0,
    "data-gpu-union-program-cache-policy": "topology-keyed-retained-material",
  };
}

function disposeMaterial(material: THREE.Material, retainers?: LoomGpuProgramRetainers): void {
  if (retainers && typeof material.userData.loomGpuUnionPlanSignature === "string") {
    const key = material.customProgramCacheKey();
    if (!retainers.has(key)) { retainers.set(key, material); return; }
    if (retainers.get(key) === material) return;
  }
  material.dispose();
}

export function disposeLoomViewerObject(
  object: THREE.Object3D,
  retainers?: LoomGpuProgramRetainers,
): void {
  object.traverse((child) => {
    const renderable = child as THREE.Object3D & {
      geometry?: THREE.BufferGeometry; material?: THREE.Material | THREE.Material[];
    };
    renderable.geometry?.dispose();
    if (Array.isArray(renderable.material)) {
      renderable.material.forEach((material) => disposeMaterial(material, retainers));
    } else if (renderable.material) disposeMaterial(renderable.material, retainers);
  });
}

export function releaseLoomGpuProgramRetainers(
  retainers: LoomGpuProgramRetainers,
  keepPlanSignature?: string,
): void {
  retainers.forEach((material, key) => {
    if (material.userData.loomGpuUnionPlanSignature === keepPlanSignature) return;
    material.dispose();
    retainers.delete(key);
  });
}

export function observeLoomGpuUnionLongTasks(host: HTMLElement): () => void {
  if (typeof PerformanceObserver === "undefined"
      || !PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
    host.dataset.gpuUnionLongTaskCount = "unsupported";
    return () => undefined;
  }
  host.dataset.gpuUnionLongTaskCount = "0";
  const observer = new PerformanceObserver((list) => {
    if (host.dataset.transientUnionBackend !== LOOM_GPU_UNION_BACKEND) return;
    host.dataset.gpuUnionLongTaskCount = String(
      Number(host.dataset.gpuUnionLongTaskCount ?? 0) + list.getEntries().length,
    );
  });
  observer.observe({ entryTypes: ["longtask"] });
  return () => observer.disconnect();
}

export function recordLoomGpuUnionFrame(host: HTMLElement, startedAt: number): void {
  if (host.dataset.transientUnionBackend !== LOOM_GPU_UNION_BACKEND) return;
  const elapsed = performance.now() - startedAt;
  host.dataset.gpuUnionLastFrameMs = elapsed.toFixed(3);
  host.dataset.gpuUnionMaxFrameMs = Math.max(
    Number(host.dataset.gpuUnionMaxFrameMs ?? 0), elapsed,
  ).toFixed(3);
}
